import { alpha } from '@mui/material/styles';
import { DASHBOARD_ACCENT, getDashboardDarkSurfaceTokens } from './dashboardSurfaceSx';
import {
  getDrawerAutocompleteSlotProps,
  getDrawerDatePickerSlotProps,
  getDrawerSelectMenuProps,
} from './drawerOverlayProps';

export const BOOKING_DRAWER_ACCENT = DASHBOARD_ACCENT;

/** Room card thumbnail width — narrower on mobile drawer only */
export const BOOKING_DRAWER_ROOM_IMAGE_WIDTH = 120;
export const BOOKING_DRAWER_ROOM_IMAGE_WIDTH_MOBILE = 96;

export const bookingDrawerRoomImageSizeSx = {
  width: { xs: BOOKING_DRAWER_ROOM_IMAGE_WIDTH_MOBILE, sm: BOOKING_DRAWER_ROOM_IMAGE_WIDTH },
  minWidth: { xs: BOOKING_DRAWER_ROOM_IMAGE_WIDTH_MOBILE, sm: BOOKING_DRAWER_ROOM_IMAGE_WIDTH },
  flexShrink: 0,
};

export const BOOKING_DRAWER_WIDTH_SX = { xs: '100%', sm: 600 };

/** Matches QuickBookingDrawer paper width — use for nested overlays opened from quick book. */
export const QUICK_BOOKING_DRAWER_WIDTH_SX = {
  xs: '100%',
  sm: 'min(660px, 100vw)',
  md: 660,
};

export function getBookingDrawerTheme(isDark) {
  return getDashboardDarkSurfaceTokens(isDark);
}

export function getBookingDrawerPaperSx(isDark, extra = {}) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    background: isDark ? tokens.surfaceGradient : '#ffffff',
    backgroundColor: isDark ? tokens.surface : '#ffffff',
    borderLeft: isDark ? `1px solid ${tokens.surfaceBorder}` : undefined,
    ...extra,
  };
}

export function getBookingDrawerHeaderSx(isDark) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    borderBottom: `2px solid ${tokens.surfaceBorderStrong}`,
    backgroundColor: tokens.headerBg,
  };
}

export function getBookingDrawerFooterSx(isDark) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    borderTop: `2px solid ${tokens.surfaceBorderStrong}`,
    backgroundColor: tokens.headerBg,
  };
}

export function getBookingDrawerPanelSx(isDark, extra = {}) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    background: isDark ? tokens.surfaceGradient : '#ffffff',
    backgroundColor: isDark ? tokens.surface : '#ffffff',
    border: `1px solid ${isDark ? alpha(BOOKING_DRAWER_ACCENT, 0.18) : alpha(BOOKING_DRAWER_ACCENT, 0.14)}`,
    borderRadius: '12px',
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    ...extra,
  };
}

export function getBookingDrawerOutlinedButtonSx(isDark) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    borderColor: tokens.surfaceBorder,
    color: tokens.subtleText,
    '&:hover': {
      borderColor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.45 : 0.4),
      backgroundColor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.1 : 0.06),
      color: isDark ? tokens.text : '#0f172a',
    },
    '&:disabled': {
      borderColor: tokens.surfaceBorder,
      color: alpha(tokens.subtleText, 0.55),
    },
  };
}

export function getBookingDrawerPrimaryButtonSx(isDark) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    backgroundColor: BOOKING_DRAWER_ACCENT,
    color: '#04120e',
    fontWeight: 700,
    boxShadow: isDark ? `0 8px 20px ${alpha(BOOKING_DRAWER_ACCENT, 0.22)}` : undefined,
    '&:hover': {
      backgroundColor: BOOKING_DRAWER_ACCENT,
      opacity: 0.92,
    },
    '&:disabled': {
      backgroundColor: isDark ? alpha('#ffffff', 0.08) : '#e0e0e0',
      color: isDark ? alpha(tokens.subtleText, 0.55) : '#757575',
      boxShadow: 'none',
    },
  };
}

export function getBookingDrawerAuditButtonSx(isDark) {
  return {
    borderColor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.35 : 0.4),
    color: BOOKING_DRAWER_ACCENT,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: '999px',
    '&:hover': {
      borderColor: BOOKING_DRAWER_ACCENT,
      backgroundColor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.12 : 0.08),
    },
    '&.Mui-disabled': {
      borderColor: getBookingDrawerTheme(isDark).surfaceBorder,
      color: alpha(getBookingDrawerTheme(isDark).subtleText, 0.55),
    },
  };
}

export function getBookingDrawerMetaChipSx(isDark) {
  return {
    backgroundColor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.14 : 0.1),
    color: BOOKING_DRAWER_ACCENT,
    fontWeight: 600,
    border: `1px solid ${alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.32 : 0.24)}`,
  };
}

export function getBookingDrawerSelectionChipSx(isDark, isComplete) {
  const tokens = getBookingDrawerTheme(isDark);
  if (isComplete) {
    return {
      backgroundColor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.18 : 0.14),
      color: isDark ? '#f8fafc' : '#0f172a',
      border: `1px solid ${alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.45 : 0.35)}`,
      fontWeight: 700,
    };
  }
  return {
    backgroundColor: tokens.chipBg,
    color: tokens.subtleText,
    border: `1px solid ${tokens.surfaceBorder}`,
    fontWeight: 500,
  };
}

export function getBookingDrawerRoomCardSx(isDark, { isDisabled = false } = {}) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    background: isDark ? tokens.surfaceGradient : '#ffffff',
    backgroundColor: isDark ? tokens.surface : '#ffffff',
    border: `1px solid ${tokens.surfaceBorder}`,
    borderRadius: 3,
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    cursor: !isDisabled ? 'pointer' : 'default',
    opacity: isDisabled ? 0.58 : 1,
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    '&:hover': !isDisabled
      ? {
          transform: 'translateY(-2px)',
          borderColor: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.35 : 0.3),
          boxShadow: isDark
            ? `0 10px 24px rgba(0,0,0,0.35), 0 0 0 1px ${alpha(BOOKING_DRAWER_ACCENT, 0.12)}`
            : `0 8px 20px ${alpha(BOOKING_DRAWER_ACCENT, 0.15)}`,
        }
      : {},
  };
}

export function getBookingDrawerDateBoxSx(isDark) {
  return {
    p: 1,
    borderRadius: '8px',
    background: alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.08 : 0.05),
    border: `1px solid ${alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.2 : 0.15)}`,
  };
}

export function getBookingDrawerRoomCardFooterSx(isDark) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    p: 1.25,
    backgroundColor: isDark ? tokens.headerBg : alpha('#ffffff', 0.95),
    backdropFilter: 'blur(10px)',
    borderTop: `1px solid ${tokens.surfaceBorder}`,
  };
}

export function getBookingDrawerInputBg(isDark) {
  return isDark ? getBookingDrawerTheme(isDark).chipBg : '#f5f5f5';
}

export function getBookingDrawerDividerColor(isDark) {
  return getBookingDrawerTheme(isDark).surfaceBorder;
}

export function getBookingDrawerDividerSx(isDark) {
  return { borderColor: getBookingDrawerDividerColor(isDark) };
}

export function getBookingDrawerTextFieldSx(isDark) {
  const tokens = getBookingDrawerTheme(isDark);
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: isDark ? tokens.chipBg : '#f5f5f5',
      '& fieldset': {
        borderColor: isDark ? tokens.surfaceBorder : 'rgba(0, 0, 0, 0.1)',
      },
    },
  };
}

/** Default outlined border for guest drawer inputs */
export function getGuestDrawerFieldBorderColor(isDark) {
  const tokens = getBookingDrawerTheme(isDark);
  return isDark ? tokens.surfaceBorder : 'rgba(0, 0, 0, 0.23)';
}

/** Walk-in guest details border color (GuestDetailsStep) */
export function getWalkInGuestInputBorderColor(isDark) {
  return isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)';
}

/** Scroll area sx — uniform transparent inputs like walk-in guest details */
export function getGuestDrawerContentSx(isDark) {
  const tokens = getBookingDrawerTheme(isDark);
  const borderColor = getWalkInGuestInputBorderColor(isDark);
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      '& fieldset': { borderColor },
      '&:hover fieldset': { borderColor: BOOKING_DRAWER_ACCENT },
      '&.Mui-focused fieldset': { borderColor: BOOKING_DRAWER_ACCENT },
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: BOOKING_DRAWER_ACCENT,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: BOOKING_DRAWER_ACCENT,
    },
    '& .MuiInputBase-input, & .MuiSelect-select': {
      backgroundColor: 'transparent !important',
      color: isDark ? tokens.text : '#333',
      fontSize: '16px',
    },
    '& .MuiInputLabel-root': {
      color: isDark ? tokens.subtleText : '#666',
      '&.Mui-focused': { color: BOOKING_DRAWER_ACCENT },
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
      backgroundColor: isDark ? tokens.surface : '#ffffff',
      px: 0.5,
      zIndex: 1,
    },
    '& .MuiFormControl-root': {
      overflow: 'visible',
    },
  };
}

export function getReservationOutlinedFieldSx(isDark, surfaceBg = isDark ? '#2a2a31' : '#ffffff') {
  const accent = BOOKING_DRAWER_ACCENT;
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      overflow: 'visible',
    },
    '& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset': {
      borderColor: accent,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root:hover fieldset': {
      borderColor: accent,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused fieldset': {
      borderColor: accent,
    },
    '& .MuiInputBase-input, & .MuiSelect-select': {
      backgroundColor: 'transparent !important',
      color: isDark ? '#ddd' : '#333',
      fontSize: '16px',
    },
    '& .MuiInputLabel-root': {
      color: isDark ? '#999' : '#666',
      zIndex: 1,
      '&.Mui-focused': { color: accent },
    },
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
      backgroundColor: surfaceBg,
      px: 0.75,
      ml: -0.25,
      zIndex: 1,
    },
    '& .MuiFormControl-root': {
      overflow: 'visible',
    },
  };
}

/**
 * Select / Menu lists opened from booking drawers.
 * Mobile shell sets MuiDrawer z-index to 1700, so menus must portal above DRAWER_MENU
 * (1800) — in-drawer menus (disablePortal) are clipped by overflow:auto content.
 */
export function getBookingDrawerSelectMenuProps(isDark, surfaceBg) {
  const bg = surfaceBg ?? (isDark ? getBookingDrawerTheme(isDark).surface : '#ffffff');
  return getDrawerSelectMenuProps(isDark, bg);
}

/** Alias — MUI Menu uses the same props shape as Select menus */
export function getBookingDrawerMenuProps(isDark, surfaceBg) {
  return getBookingDrawerSelectMenuProps(isDark, surfaceBg);
}

export function getBookingDrawerAutocompleteSlotProps(isDark, surfaceBg) {
  const bg = surfaceBg ?? (isDark ? getBookingDrawerTheme(isDark).surface : '#ffffff');
  return getDrawerAutocompleteSlotProps(isDark, bg);
}

export function getBookingDrawerDatePickerSlotProps(isDark, surfaceBg) {
  const bg = surfaceBg ?? (isDark ? getBookingDrawerTheme(isDark).surface : '#ffffff');
  return getDrawerDatePickerSlotProps(isDark, bg);
}

export const bookingDrawerOverlayFieldShellSx = {
  '& .MuiFormControl-root': { overflow: 'visible' },
  '& .MuiAutocomplete-root': { overflow: 'visible' },
};

/** Bundle overlay props for Select, Menu, Autocomplete, and DatePicker in drawers */
export function createBookingDrawerOverlayProps(isDark, surfaceBg) {
  const menuProps = getBookingDrawerSelectMenuProps(isDark, surfaceBg);
  return {
    selectMenuProps: menuProps,
    menuProps,
    autocompleteSlotProps: getBookingDrawerAutocompleteSlotProps(isDark, surfaceBg),
    datePickerSlotProps: getBookingDrawerDatePickerSlotProps(isDark, surfaceBg),
    fieldShellSx: bookingDrawerOverlayFieldShellSx,
  };
}

/** Merge custom Paper sx into drawer menu props (e.g. phone code picker width) */
export function withDrawerMenuPaperSx(menuProps, paperSx) {
  if (!menuProps) {
    return { PaperProps: { sx: paperSx } };
  }
  return {
    ...menuProps,
    PaperProps: {
      ...menuProps.PaperProps,
      sx: {
        ...menuProps.PaperProps?.sx,
        ...paperSx,
      },
    },
  };
}

/** TextField / Autocomplete sx for add-guest drawers */
export function getGuestDrawerFieldSx(isDark, hasError = false, errorColor = '#d32f2f') {
  const tokens = getBookingDrawerTheme(isDark);
  const borderColor = hasError ? errorColor : getWalkInGuestInputBorderColor(isDark);
  const focusBorder = hasError ? errorColor : BOOKING_DRAWER_ACCENT;
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      '& fieldset': { borderColor },
      '&:hover fieldset': { borderColor: focusBorder },
      '&.Mui-focused fieldset': { borderColor: focusBorder },
    },
    '& .MuiInputBase-input': {
      backgroundColor: 'transparent !important',
      color: isDark ? tokens.text : '#333',
      fontSize: '16px',
    },
    '& .MuiInputLabel-root': {
      color: isDark ? tokens.subtleText : '#666',
      '&.Mui-focused': { color: hasError ? errorColor : BOOKING_DRAWER_ACCENT },
    },
  };
}

/** Select / FormControl sx for add-guest drawers */
export function getGuestDrawerSelectSx(isDark, hasError = false, errorColor = '#d32f2f') {
  const borderColor = hasError ? errorColor : getWalkInGuestInputBorderColor(isDark);
  const focusBorder = hasError ? errorColor : BOOKING_DRAWER_ACCENT;
  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
    },
    '& .MuiSelect-select': {
      backgroundColor: 'transparent !important',
      fontSize: '16px',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: focusBorder },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: focusBorder },
  };
}

/** ID document upload drop zone */
export function getGuestDrawerUploadPaperSx(isDark, extra = {}) {
  return {
    p: 2,
    border: `2px dashed ${alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.55 : 0.45)}`,
    borderRadius: 2,
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: isDark ? alpha(BOOKING_DRAWER_ACCENT, 0.06) : alpha(BOOKING_DRAWER_ACCENT, 0.04),
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s ease, border-color 0.2s ease',
    '&:hover': {
      backgroundColor: isDark ? alpha(BOOKING_DRAWER_ACCENT, 0.1) : alpha(BOOKING_DRAWER_ACCENT, 0.08),
      borderColor: BOOKING_DRAWER_ACCENT,
    },
    ...extra,
  };
}

/** Booking code / room badge in guest drawer header */
export function getGuestDrawerMetaChipSx(isDark) {
  return {
    backgroundColor: BOOKING_DRAWER_ACCENT,
    color: '#04120e',
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24,
  };
}

export function getBookingDrawerErrorPaperSx(isDark, extra = {}) {
  return getBookingDrawerPanelSx(isDark, {
    borderRadius: 2,
    boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.2)',
    ...extra,
  });
}

/** Standard drawer shell: pass to Drawer PaperProps.sx */
export function getAppDrawerPaperSx(isDark, extra = {}) {
  return getBookingDrawerPaperSx(isDark, extra);
}

export function getAppDrawerHeaderSx(isDark, extra = {}) {
  return { p: 1.5, ...getBookingDrawerHeaderSx(isDark), ...extra };
}

export function getAppDrawerFooterSx(isDark, extra = {}) {
  return { p: 1.5, ...getBookingDrawerFooterSx(isDark), ...extra };
}
